import { ModerationBoundaryTemplate, Queue } from '../../models/Moderation'

export type ModerationTemplate = {
    id: number
    displayName: string
    severe: Map<string, ModerationBoundaryTemplate>
    suspect: Map<string, ModerationBoundaryTemplate>
    safe: Map<string, ModerationBoundaryTemplate>
}

function generateTemplateFamily(
    queue: Queue,
    options: QueueBoundaryTypes
): Map<string, ModerationBoundaryTemplate> {
    const template = new Map<string, ModerationBoundaryTemplate>()

    // We need this test as if the value is zero JS considers it falsy .
    function isNotNullOrUndefined(value: any): boolean {
        return value !== null && value !== undefined
    }

    if (isNotNullOrUndefined(options.identityAttack)) {
        template.set('IDENTITY_ATTACK'.toLowerCase(), {
            classifierName: 'IDENTITY_ATTACK',
            queue: queue,
            probability: options.identityAttack
        })
    }
    if (isNotNullOrUndefined(options.toxicity)) {
        template.set('TOXICITY'.toLowerCase(), {
            classifierName: 'TOXICITY',
            queue: queue,
            probability: options.toxicity
        })
    }
    if (isNotNullOrUndefined(options.profanity)) {
        template.set('PROFANITY'.toLowerCase(), {
            classifierName: 'PROFANITY',
            queue: queue,
            probability: options.profanity
        })
    }
    if (isNotNullOrUndefined(options.sexuallyExplicit)) {
        template.set('SEXUALLY_EXPLICIT'.toLowerCase(), {
            classifierName: 'SEXUALLY_EXPLICIT',
            queue: queue,
            probability: options.sexuallyExplicit
        })
    }
    if (isNotNullOrUndefined(options.insultPerspective)) {
        template.set('INSULT'.toLowerCase(), {
            classifierName: 'INSULT',
            queue: queue,
            probability: options.insultPerspective
        })
    }
    if (isNotNullOrUndefined(options.severeToxicity)) {
        template.set('SEVERE_TOXICITY'.toLowerCase(), {
            classifierName: 'SEVERE_TOXICITY',
            queue: queue,
            probability: options.severeToxicity
        })
    }
    if (isNotNullOrUndefined(options.threat)) {
        template.set('THREAT'.toLowerCase(), {
            classifierName: 'THREAT',
            queue: queue,
            probability: options.threat
        })
    }
    if (isNotNullOrUndefined(options.spam)) {
        template.set('SPAM'.toLowerCase(), {
            classifierName: 'SPAM',
            queue: queue,
            probability: options.spam
        })
    }
    // Keywords
    if (isNotNullOrUndefined(options.blockedKeyword)) {
        template.set('BLOCKED_KEYWORDS'.toLowerCase(), {
            classifierName: 'BLOCKED_KEYWORDS',
            queue: queue,
            probability: options.blockedKeyword
        })
    }
    if (isNotNullOrUndefined(options.racistKeyword)) {
        template.set('KEYWORD_RACIST'.toLowerCase(), {
            classifierName: 'KEYWORD_RACIST',
            queue: queue,
            probability: options.racistKeyword
        })
    }
    // Image classifiers
    if (isNotNullOrUndefined(options.pornImage)) {
        template.set('IMAGEPORN'.toLowerCase(), {
            classifierName: 'IMAGEPORN',
            queue: queue,
            probability: options.pornImage
        })
    }
    if (isNotNullOrUndefined(options.drugsImage)) {
        template.set('IMAGEDRUGS'.toLowerCase(), {
            classifierName: 'IMAGEDRUGS',
            queue: queue,
            probability: options.drugsImage
        })
    }
    if (isNotNullOrUndefined(options.alcoholImage)) {
        template.set('IMAGEALCOHOL'.toLowerCase(), {
            classifierName: 'IMAGEALCOHOL',
            queue: queue,
            probability: options.alcoholImage
        })
    }
    if (isNotNullOrUndefined(options.weaponsImage)) {
        template.set('IMAGEWEAPONS'.toLowerCase(), {
            classifierName: 'IMAGEWEAPONS',
            queue: queue,
            probability: options.weaponsImage
        })
    }
    if (isNotNullOrUndefined(options.gamblingImage)) {
        template.set('IMAGEGAMBLING'.toLowerCase(), {
            classifierName: 'IMAGEGAMBLING',
            queue: queue,
            probability: options.gamblingImage
        })
    }
    if (isNotNullOrUndefined(options.swimUnderwearImage)) {
        template.set('IMAGESWIMUNDERWEAR'.toLowerCase(), {
            classifierName: 'IMAGESWIMUNDERWEAR',
            queue: queue,
            probability: options.swimUnderwearImage
        })
    }
    if (isNotNullOrUndefined(options.offensiveGesturesImage)) {
        template.set('IMAGEOFFENSIVEGESTURES'.toLowerCase(), {
            classifierName: 'IMAGEOFFENSIVEGESTURES',
            queue: queue,
            probability: options.offensiveGesturesImage
        })
    }

    return template
}

type QueueBoundaryTypes = {
    identityAttack?: number
    toxicity?: number
    profanity?: number
    sexuallyExplicit?: number
    insultPerspective?: number
    severeToxicity?: number
    threat?: number
    spam?: number
    blockedKeyword?: number
    racistKeyword?: number
    pornImage?: number
    swimUnderwearImage?: number
    drugsImage?: number
    alcoholImage?: number
    offensiveGesturesImage?: number
    gamblingImage?: number
    weaponsImage?: number
}

export default function getModerationTemplates(
    socialMediaServiceShortName?: string // twitter facebook instagram youtube tiktook
) {
    const isTwitter = socialMediaServiceShortName === 'twitter'
    const isYouTube = socialMediaServiceShortName === 'youtube'

    return [
        {
            id: 0,
            displayName: 'Family',
            severe: generateTemplateFamily('SEVERE', {
                identityAttack: 0.7,
                toxicity: 0.8,
                profanity: 0.8,
                sexuallyExplicit: 0.7,
                insultPerspective: 0.8,
                severeToxicity: 0.6,
                threat: 0.7,
                spam: isTwitter || isYouTube ? 0.98 : 0.8,
                blockedKeyword: 1,
                racistKeyword: 1,
                pornImage: 0.5,
                swimUnderwearImage: 0.5,
                drugsImage: 0.5,
                alcoholImage: 0.5,
                offensiveGesturesImage: 0.5,
                gamblingImage: 0.5,
                weaponsImage: 0.5
            }),
            suspect: generateTemplateFamily('SUSPECT', {
                identityAttack: 0.6,
                toxicity: 0.7,
                profanity: 0.7,
                sexuallyExplicit: 0.65,
                insultPerspective: 0.7,
                severeToxicity: 0.5,
                threat: 0.65,
                spam: isTwitter || isYouTube ? 0.9 : 0.7,
                pornImage: 0.25,
                swimUnderwearImage: 0.25,
                drugsImage: 0.25,
                alcoholImage: 0.25,
                offensiveGesturesImage: 0.25,
                gamblingImage: 0.25,
                weaponsImage: 0.25
            }),
            safe: generateTemplateFamily('SAFE', {
                identityAttack: 0,
                toxicity: 0,
                profanity: 0,
                sexuallyExplicit: 0,
                insultPerspective: 0,
                severeToxicity: 0,
                threat: 0,
                spam: 0,
                blockedKeyword: 0,
                racistKeyword: 0,
                pornImage: 0,
                swimUnderwearImage: 0,
                drugsImage: 0,
                alcoholImage: 0,
                offensiveGesturesImage: 0,
                gamblingImage: 0,
                weaponsImage: 0
            })
        },
        {
            id: 1,
            displayName: 'Standard',
            severe: generateTemplateFamily('SEVERE', {
                identityAttack: 0.8,
                toxicity: 0.9,
                profanity: 0.9,
                sexuallyExplicit: 0.8,
                insultPerspective: 0.9,
                severeToxicity: 0.7,
                threat: 0.9,
                spam: isTwitter || isYouTube ? 0.98 : 0.8,
                blockedKeyword: 1,
                racistKeyword: 1,
                pornImage: 0.5
            }),
            suspect: generateTemplateFamily('SUSPECT', {
                identityAttack: 0.7,
                toxicity: 0.8,
                profanity: 0.8,
                sexuallyExplicit: 0.7,
                insultPerspective: 0.8,
                severeToxicity: 0.65,
                threat: 0.8,
                spam: isTwitter || isYouTube ? 0.9 : 0.7,
                pornImage: 0.25
            }),
            safe: generateTemplateFamily('SAFE', {
                identityAttack: 0,
                toxicity: 0,
                profanity: 0,
                sexuallyExplicit: 0,
                insultPerspective: 0,
                severeToxicity: 0,
                threat: 0,
                spam: 0,
                blockedKeyword: 0,
                racistKeyword: 0,
                pornImage: 0,
                swimUnderwearImage: 0,
                drugsImage: 0,
                alcoholImage: 0,
                offensiveGesturesImage: 0,
                gamblingImage: 0,
                weaponsImage: 0
            })
        },
        {
            id: 2,
            displayName: 'Sport',
            severe: generateTemplateFamily('SEVERE', {
                identityAttack: 0.8,
                toxicity: 0.9,
                profanity: 0.95,
                sexuallyExplicit: 0.9,
                insultPerspective: 0.95,
                severeToxicity: 0.75,
                threat: 1.01,
                spam: isTwitter || isYouTube ? 0.98 : 0.8,
                blockedKeyword: 1,
                racistKeyword: 1,
                pornImage: 0.5
            }),
            suspect: generateTemplateFamily('SUSPECT', {
                identityAttack: 0.7,
                toxicity: 0.8,
                profanity: 0.85,
                sexuallyExplicit: 0.7,
                insultPerspective: 0.8,
                severeToxicity: 0.65,
                threat: 0.9,
                spam: isTwitter || isYouTube ? 0.9 : 0.7,
                blockedKeyword: 1,
                racistKeyword: 1,
                pornImage: 0.25
            }),
            safe: generateTemplateFamily('SAFE', {
                identityAttack: 0,
                toxicity: 0,
                profanity: 0,
                sexuallyExplicit: 0,
                insultPerspective: 0,
                severeToxicity: 0,
                threat: 0,
                spam: 0,
                blockedKeyword: 0,
                racistKeyword: 0,
                pornImage: 0
            })
        },
        {
            id: 3,
            displayName: 'Spam only',
            severe: generateTemplateFamily('SEVERE', {
                spam: isTwitter || isYouTube ? 0.98 : 0.8
            }),
            suspect: generateTemplateFamily('SUSPECT', {
                spam: isTwitter || isYouTube ? 0.9 : 0.7
            }),
            safe: generateTemplateFamily('SAFE', {
                spam: 0
            })
        }
    ]
}
